// ============================================================
// Chess Arena — Tutorial Manager
// ============================================================
// Manages the tutorial overlay, spotlight system, step
// sequencing, enemy freeze control, speech bubbles (AI banter),
// and post-onboarding main-menu tutorial events.
// ============================================================

import Phaser from 'phaser';
import {
  LevelConfig,
  TutorialStep,
  TutorialLevelConfig,
  TutorialDialogue,
} from '../core/types';
import eventBus from '../core/eventBus';
import { playerState } from '../core/playerState';
import gameConfig from '../core/gameConfig';

// --- Visual constants ---

const OVERLAY_ALPHA = 0.55;
const OVERLAY_COLOR = 0x000000;
const SPOTLIGHT_PADDING = 24;
const SPOTLIGHT_BORDER_COLOR = 0xffd700;
const SPOTLIGHT_BORDER_WIDTH = 3;
const SPEECH_BUBBLE_BG = 0x1a1a2e;
const SPEECH_BUBBLE_BORDER = 0x5577dd;
const SPEECH_BUBBLE_RADIUS = 12;
const TEXT_POPUP_BG = 0x111122;
const TEXT_POPUP_BORDER = 0x888888;

export class TutorialManager {
  private scene: Phaser.Scene;
  private levelConfig!: LevelConfig;
  private tutorialConfig!: TutorialLevelConfig;

  // State
  private currentStepIndex = -1;
  private active = false;
  private enemiesFrozen = false;
  private _cooldownsPaused = false;
  private cooldownPauseTimer: Phaser.Time.TimerEvent | null = null;

  // Overlay layer
  private overlayContainer!: Phaser.GameObjects.Container;
  private overlayGraphics!: Phaser.GameObjects.Graphics;
  private spotlightBorder!: Phaser.GameObjects.Graphics;
  private handPointer!: Phaser.GameObjects.Image;
  private textPopup!: Phaser.GameObjects.Container;
  private textContent!: Phaser.GameObjects.Text;
  private tapHint!: Phaser.GameObjects.Text;

  // Mascot portrait
  private mascotImage!: Phaser.GameObjects.Image;

  // Speech bubble elements
  private speechBubble: Phaser.GameObjects.Container | null = null;
  private speechBubbleTimeout: Phaser.Time.TimerEvent | null = null;

  // Dialogue triggers (for Level 5 AI banter)
  private dialogueTriggers: TutorialDialogue[] = [];
  private boundDialogueHandlers: Map<string, (data?: Record<string, unknown>) => void> = new Map();

  // Bound event handler for action gating
  private boundActionHandler: ((data?: Record<string, unknown>) => void) | null = null;
  private requiredAction: string | null = null;

  // Callback to resolve spotlight targets to screen rects
  private spotlightResolver: ((target: string) => Phaser.Geom.Rectangle | null) | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------
  // Initialisation
  // ------------------------------------------------------------------

  /**
   * Initialise the tutorial system for a level.
   * @param levelConfig  The current level's configuration.
   * @param tutorialConfig  Tutorial step/dialogue config for this level.
   * @param spotlightResolver  A callback that converts a spotlightTarget string
   *   (e.g. "playerKnight", "enemyKing", "spawnBar") into a screen-space rectangle.
   *   The BattleScene provides this based on its knowledge of piece and UI positions.
   */
  init(
    levelConfig: LevelConfig,
    tutorialConfig: TutorialLevelConfig,
    spotlightResolver?: (target: string) => Phaser.Geom.Rectangle | null,
  ): void {
    this.levelConfig = levelConfig;
    this.tutorialConfig = tutorialConfig;
    this.spotlightResolver = spotlightResolver ?? null;

    this.currentStepIndex = -1;
    this.active = false;
    this.enemiesFrozen = false;

    this.createOverlayElements();
    this.registerDialogueTriggers(tutorialConfig.aiDialogue ?? []);
  }

  // ------------------------------------------------------------------
  // Overlay creation
  // ------------------------------------------------------------------

  private createOverlayElements(): void {
    const { width, height } = this.scene.cameras.main;

    this.overlayContainer = this.scene.add.container(0, 0);
    this.overlayContainer.setDepth(200);
    this.overlayContainer.setVisible(false);

    // Full-screen dark overlay
    this.overlayGraphics = this.scene.add.graphics();
    this.overlayContainer.add(this.overlayGraphics);

    // Spotlight border (gold pulsing)
    this.spotlightBorder = this.scene.add.graphics();
    this.overlayContainer.add(this.spotlightBorder);

    // Hand pointer
    const handKey = this.scene.textures.exists('tutorial_hand_pointer')
      ? 'tutorial_hand_pointer'
      : '';
    if (handKey) {
      this.handPointer = this.scene.add.image(0, 0, handKey);
      this.handPointer.setDisplaySize(72, 72);
      this.handPointer.setVisible(false);
      this.overlayContainer.add(this.handPointer);
    } else {
      // Fallback: create a minimal placeholder
      this.handPointer = this.scene.add.image(0, 0, '__DEFAULT');
      this.handPointer.setVisible(false);
    }

    // Text popup container
    // Position text just below the board area (board ends around 85% height)
    const mascotSize = 72;
    const mascotGap = 10;
    const textAreaWidth = width * 0.75 - mascotSize - mascotGap;

    this.textPopup = this.scene.add.container(width / 2, height * 0.82);
    this.overlayContainer.add(this.textPopup);

    // Text popup background
    const popupBg = this.scene.add.graphics();
    this.textPopup.add(popupBg);

    // Mascot King portrait (left side of popup)
    if (this.scene.textures.exists('tutorial_mascot_king')) {
      this.mascotImage = this.scene.add.image(0, 0, 'tutorial_mascot_king');
      this.mascotImage.setDisplaySize(mascotSize, mascotSize);
      this.textPopup.add(this.mascotImage);
    }

    // Text content (shifted right to make room for mascot)
    this.textContent = this.scene.add.text(0, 0, '', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '28px',
      color: '#ffffff',
      align: 'left',
      wordWrap: { width: textAreaWidth },
    }).setOrigin(0.5);
    this.textPopup.add(this.textContent);

    // "Tap anywhere to continue" hint (shown for info-only steps)
    this.tapHint = this.scene.add.text(0, 0, 'Tap anywhere to continue', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '18px',
      color: '#aaaacc',
      align: 'center',
    }).setOrigin(0.5).setAlpha(0.7);
    this.textPopup.add(this.tapHint);
  }

  // ------------------------------------------------------------------
  // Tutorial flow
  // ------------------------------------------------------------------

  startTutorial(): void {
    if (this.tutorialConfig.steps.length === 0) return;

    this.active = true;
    this.currentStepIndex = -1;
    this.advanceStep();
  }

  advanceStep(): void {
    // Cleanup previous action handler
    this.clearActionHandler();
    this._cooldownsPaused = false;
    if (this.cooldownPauseTimer) {
      this.cooldownPauseTimer.destroy();
      this.cooldownPauseTimer = null;
    }

    this.currentStepIndex++;

    if (this.currentStepIndex >= this.tutorialConfig.steps.length) {
      this.completeTutorial();
      return;
    }

    const step = this.tutorialConfig.steps[this.currentStepIndex];
    this.showStep(step);
  }

  isActive(): boolean {
    return this.active;
  }

  getCurrentStep(): TutorialStep | null {
    if (!this.active || this.currentStepIndex < 0 || this.currentStepIndex >= this.tutorialConfig.steps.length) {
      return null;
    }
    return this.tutorialConfig.steps[this.currentStepIndex];
  }

  getNextStep(): TutorialStep | null {
    const nextIdx = this.currentStepIndex + 1;
    if (!this.active || nextIdx < 0 || nextIdx >= this.tutorialConfig.steps.length) {
      return null;
    }
    return this.tutorialConfig.steps[nextIdx];
  }

  /** Look ahead past info-only steps to find the next step that requires a player action. */
  getNextActionStep(): TutorialStep | null {
    if (!this.active) return null;
    for (let i = this.currentStepIndex + 1; i < this.tutorialConfig.steps.length; i++) {
      const step = this.tutorialConfig.steps[i];
      if (step.requiredAction) return step;
    }
    return null;
  }

  areEnemiesFrozen(): boolean {
    return this.enemiesFrozen;
  }

  areCooldownsPaused(): boolean {
    return this._cooldownsPaused;
  }

  /**
   * Returns the spotlightTarget string for the current step if it requires
   * an action (i.e. the player must interact). Returns null if no restriction.
   */
  getAllowedInteractionTarget(): string | null {
    const step = this.getCurrentStep();
    if (!step) return null;
    // Block all piece interaction during info-only (tap-to-continue) steps
    if (!step.requiredAction) return '__blocked__';
    if (step.blockInteraction) return '__blocked__';
    return step.spotlightTarget;
  }

  /**
   * Called by BattleScene when the player starts dragging a piece during
   * an action-gated tutorial step. Hides the dark overlay so the board
   * "lights up" and the player can see valid move highlights.
   */
  onPlayerInteracting(): void {
    if (!this.active) return;
    this.overlayContainer.setAlpha(0);
  }

  /**
   * Called when the player finishes interacting (drop). If the step
   * hasn't advanced (e.g. wrong move), re-show the overlay.
   */
  onPlayerStoppedInteracting(): void {
    if (!this.active) return;
    this.overlayContainer.setAlpha(1);
  }

  // ------------------------------------------------------------------
  // Show a tutorial step
  // ------------------------------------------------------------------

  private showStep(step: TutorialStep): void {
    const { width, height } = this.scene.cameras.main;

    // Control enemy freeze state
    this.enemiesFrozen = step.enemyState === 'frozen';

    // Pause cooldowns after a short delay for info-only steps so the
    // player can see the cooldown bar partially filled before it freezes.
    if (this.cooldownPauseTimer) {
      this.cooldownPauseTimer.destroy();
      this.cooldownPauseTimer = null;
    }
    if (!step.requiredAction || step.pauseCooldowns) {
      this.cooldownPauseTimer = this.scene.time.delayedCall(500, () => {
        this._cooldownsPaused = true;
      });
    } else {
      this._cooldownsPaused = false;
    }

    // Show overlay container (holds text popup, hand pointer, etc.)
    this.overlayContainer.setVisible(true);

    // For certain steps, skip the dark overlay so the board is fully visible
    // (e.g. during bomb explosion, targeting mode)
    const skipDarkOverlay =
      step.requiredAction === 'targeting_mode_confirm' ||
      step.stepId === 'bomb_success';
    if (skipDarkOverlay) {
      this.overlayGraphics.clear();
      this.spotlightBorder.clear();
    } else {
      // Draw overlay with spotlight cutout
      this.drawOverlay(step.spotlightTarget);
    }

    // Update text
    this.textContent.setText(step.text);

    // Show/hide tap hint
    const showHint = !step.requiredAction;
    this.tapHint.setVisible(showHint);

    // Position popup near the spotlight target
    this.positionPopupNearSpotlight(step.spotlightTarget);

    // Redraw text popup background around the text
    this.drawTextPopupBackground();

    // Position and show hand pointer if there is a required action
    if (step.requiredAction) {
      this.showHandPointer(step.spotlightTarget);
    } else {
      this.handPointer.setVisible(false);
    }

    // Show AI dialogue if present in this step
    if (step.aiDialogue) {
      this.showSpeechBubble(step.aiDialogue);
    }

    // Grant ability if present (tutorial ability unlock)
    if (step.grantAbility) {
      const abilityId = step.grantAbility;
      if (!playerState.isAbilityUnlocked(abilityId)) {
        playerState.unlockAbility(abilityId);
        // Get starting charges from config
        if (gameConfig.isInitialized) {
          const abilityCfg = gameConfig.getAbilityConfig();
          const cfg = abilityCfg?.abilities?.[abilityId];
          if (cfg) {
            playerState.addCharges(abilityId, cfg.startingCharges, cfg.maxCharges);
            // Auto-equip if not already
            if (!playerState.equippedAbilities.includes(abilityId)) {
              playerState.equipAbility(abilityId, abilityCfg.slots.initialSlots + 1);
            }
          }
        }
        eventBus.emit('ability_unlocked', { ability: abilityId });
      }
    }

    // Execute scripted move if present (after a short delay for visual effect)
    if (step.scriptedMove) {
      this.scene.time.delayedCall(800, () => {
        eventBus.emit('tutorial_scripted_move' as any, step.scriptedMove);
      });
    }

    // Set up interaction
    if (step.requiredAction) {
      // Wait for a specific event before advancing
      this.requiredAction = step.requiredAction;
      this.boundActionHandler = () => {
        this.advanceStep();
      };
      // The requiredAction string maps to an eventBus event name
      // (e.g. "piece_moved", "piece_captured", "premove_queued")
      eventBus.on(step.requiredAction as any, this.boundActionHandler);
    } else {
      // Info-only step: tap anywhere to continue
      this.setupTapToContinue();
    }
  }

  // ------------------------------------------------------------------
  // Overlay drawing
  // ------------------------------------------------------------------

  /**
   * Resolve a (possibly comma-separated) spotlight target string into
   * a union bounding rectangle covering all individual targets.
   */
  private resolveUnionRect(spotlightTarget: string | null): Phaser.Geom.Rectangle | null {
    if (!this.spotlightResolver || !spotlightTarget) return null;

    const targets = spotlightTarget.split(',').map((t) => t.trim());
    let union: Phaser.Geom.Rectangle | null = null;

    for (const t of targets) {
      const r = this.spotlightResolver(t);
      if (!r) continue;
      if (!union) {
        union = new Phaser.Geom.Rectangle(r.x, r.y, r.width, r.height);
      } else {
        const x1 = Math.min(union.x, r.x);
        const y1 = Math.min(union.y, r.y);
        const x2 = Math.max(union.right, r.right);
        const y2 = Math.max(union.bottom, r.bottom);
        union.setTo(x1, y1, x2 - x1, y2 - y1);
      }
    }

    return union;
  }

  private drawOverlay(spotlightTarget: string | null): void {
    const { width, height } = this.scene.cameras.main;

    this.overlayGraphics.clear();
    this.spotlightBorder.clear();
    this.scene.tweens.killTweensOf(this.spotlightBorder);

    // Full-screen dark overlay
    this.overlayGraphics.fillStyle(OVERLAY_COLOR, OVERLAY_ALPHA);
    this.overlayGraphics.fillRect(0, 0, width, height);

    // Resolve spotlight target(s) to a union rectangle
    const rect = this.resolveUnionRect(spotlightTarget);

    if (rect) {
      // Clear spotlight area (draw a transparent hole)
      // Phaser Graphics doesn't have true masking via clear, so we redraw
      // the overlay in 4 strips around the cutout
      this.overlayGraphics.clear();

      const sx = rect.x - SPOTLIGHT_PADDING;
      const sy = rect.y - SPOTLIGHT_PADDING;
      const sw = rect.width + SPOTLIGHT_PADDING * 2;
      const sh = rect.height + SPOTLIGHT_PADDING * 2;

      // Top strip
      this.overlayGraphics.fillStyle(OVERLAY_COLOR, OVERLAY_ALPHA);
      this.overlayGraphics.fillRect(0, 0, width, sy);

      // Bottom strip
      this.overlayGraphics.fillRect(0, sy + sh, width, height - (sy + sh));

      // Left strip (between top and bottom)
      this.overlayGraphics.fillRect(0, sy, sx, sh);

      // Right strip (between top and bottom)
      this.overlayGraphics.fillRect(sx + sw, sy, width - (sx + sw), sh);

      // Gold pulsing border around each individual target
      const targets = spotlightTarget!.split(',').map((t) => t.trim());
      for (const t of targets) {
        const r = this.spotlightResolver!(t);
        if (!r) continue;
        const bx = r.x - SPOTLIGHT_PADDING;
        const by = r.y - SPOTLIGHT_PADDING;
        const bw = r.width + SPOTLIGHT_PADDING * 2;
        const bh = r.height + SPOTLIGHT_PADDING * 2;
        this.spotlightBorder.lineStyle(SPOTLIGHT_BORDER_WIDTH, SPOTLIGHT_BORDER_COLOR, 1);
        this.spotlightBorder.strokeRoundedRect(bx, by, bw, bh, 8);
      }

      // Pulsing animation on the border
      this.scene.tweens.add({
        targets: this.spotlightBorder,
        alpha: { from: 1, to: 0.4 },
        yoyo: true,
        repeat: -1,
        duration: 800,
        ease: 'Sine.easeInOut',
      });
    }
  }

  // ------------------------------------------------------------------
  // Text popup background
  // ------------------------------------------------------------------

  private drawTextPopupBackground(): void {
    const padX = 20;
    const padY = 14;
    const hintGap = 8;
    const mascotSize = 72;
    const mascotGap = 10;
    const hasMascot = this.mascotImage && this.mascotImage.visible;

    // Measure main text
    const mainBounds = this.textContent.getBounds();
    let totalH = mainBounds.height;

    // Position hint below main text if visible
    if (this.tapHint.visible) {
      const hintBounds = this.tapHint.getBounds();
      this.tapHint.setY(mainBounds.height / 2 + hintGap + hintBounds.height / 2);
      totalH += hintGap + hintBounds.height;
    }

    const textW = Math.max(mainBounds.width, this.tapHint.visible ? this.tapHint.getBounds().width : 0);
    const contentW = hasMascot ? mascotSize + mascotGap + textW : textW;
    const bgW = contentW + padX * 2;
    const bgH = Math.max(totalH, hasMascot ? mascotSize : 0) + padY * 2;

    // Layout mascot on the left, text on the right
    if (hasMascot) {
      const contentLeft = -contentW / 2;
      this.mascotImage.setPosition(contentLeft + mascotSize / 2, 0);
      const textCenterX = contentLeft + mascotSize + mascotGap + textW / 2;
      const contentOffsetY = this.tapHint.visible ? -(totalH - mainBounds.height) / 2 : 0;
      this.textContent.setPosition(textCenterX, contentOffsetY);
      if (this.tapHint.visible) {
        this.tapHint.setPosition(textCenterX, contentOffsetY + mainBounds.height / 2 + hintGap + this.tapHint.getBounds().height / 2);
      }
    } else {
      const contentOffsetY = this.tapHint.visible ? -(totalH - mainBounds.height) / 2 : 0;
      this.textContent.setPosition(0, contentOffsetY);
      if (this.tapHint.visible) {
        this.tapHint.setPosition(0, contentOffsetY + mainBounds.height / 2 + hintGap + this.tapHint.getBounds().height / 2);
      }
    }

    // Access the background graphic (first child of textPopup)
    const popupBg = this.textPopup.getAt(0) as Phaser.GameObjects.Graphics;
    popupBg.clear();
    popupBg.fillStyle(TEXT_POPUP_BG, 0.92);
    popupBg.fillRoundedRect(-bgW / 2, -bgH / 2, bgW, bgH, 10);
    popupBg.lineStyle(2, TEXT_POPUP_BORDER, 0.7);
    popupBg.strokeRoundedRect(-bgW / 2, -bgH / 2, bgW, bgH, 10);
  }

  // ------------------------------------------------------------------
  // Popup positioning (near the spotlight target)
  // ------------------------------------------------------------------

  private positionPopupNearSpotlight(spotlightTarget: string | null): void {
    const { width, height } = this.scene.cameras.main;
    const rect = this.resolveUnionRect(spotlightTarget);

    if (!rect) {
      // Fallback: center-ish
      this.textPopup.setPosition(width / 2, height * 0.82);
      return;
    }

    const gap = 40; // space between spotlight and popup
    const spotlightBottom = rect.y + rect.height + SPOTLIGHT_PADDING;
    const spotlightTop = rect.y - SPOTLIGHT_PADDING;

    // Estimate popup height (main text + hint + padding)
    const estimatedH = 100;
    const spaceBelow = height - spotlightBottom;
    const spaceAbove = spotlightTop;

    let popupY: number;
    if (spaceBelow >= estimatedH + gap) {
      // Place below the spotlight
      popupY = spotlightBottom + gap + estimatedH / 2;
    } else if (spaceAbove >= estimatedH + gap) {
      // Place above the spotlight
      popupY = spotlightTop - gap - estimatedH / 2;
    } else {
      // Not enough room either way — place in whichever has more space
      popupY = spaceBelow >= spaceAbove
        ? spotlightBottom + gap + estimatedH / 2
        : spotlightTop - gap - estimatedH / 2;
    }

    // Clamp to screen bounds
    popupY = Phaser.Math.Clamp(popupY, estimatedH / 2 + 10, height - estimatedH / 2 - 10);

    this.textPopup.setPosition(width / 2, popupY);
  }

  // ------------------------------------------------------------------
  // Hand pointer
  // ------------------------------------------------------------------

  private showHandPointer(spotlightTarget: string | null): void {
    const rect = this.resolveUnionRect(spotlightTarget);
    if (!rect) {
      this.handPointer.setVisible(false);
      return;
    }

    this.handPointer.setVisible(true);
    // Place the hand centered below the target
    this.handPointer.setPosition(
      rect.x + rect.width / 2,
      rect.y + rect.height + this.handPointer.displayHeight / 2 + 4,
    );
  }

  // ------------------------------------------------------------------
  // Tap to continue (info-only steps)
  // ------------------------------------------------------------------

  private setupTapToContinue(): void {
    // Create a full-screen interactive zone
    const { width, height } = this.scene.cameras.main;
    const zone = this.scene.add.zone(width / 2, height / 2, width, height)
      .setInteractive()
      .setDepth(201);

    zone.once('pointerdown', (_pointer: Phaser.Input.Pointer, _lx: number, _ly: number, event: Phaser.Types.Input.EventData) => {
      // Stop propagation so this tap doesn't also register as a piece drag
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
      zone.destroy();
      this.advanceStep();
    });
  }

  // ------------------------------------------------------------------
  // Speech bubbles (AI banter — Level 5)
  // ------------------------------------------------------------------

  private registerDialogueTriggers(dialogues: TutorialDialogue[]): void {
    this.dialogueTriggers = dialogues;

    for (const dialogue of dialogues) {
      const handler = () => {
        this.showSpeechBubble(dialogue.text);
      };
      this.boundDialogueHandlers.set(dialogue.trigger, handler);
      // Map trigger strings to eventBus events
      // Triggers: "match_start" → 'match_played', "player_capture" → 'piece_captured',
      // "player_win" → 'king_captured'
      const eventName = this.mapTriggerToEvent(dialogue.trigger);
      if (eventName) {
        eventBus.on(eventName as any, handler);
      }
    }
  }

  private mapTriggerToEvent(trigger: string): string | null {
    const mapping: Record<string, string> = {
      'match_start': 'match_played',
      'player_capture': 'piece_captured',
      'player_win': 'king_captured',
    };
    return mapping[trigger] ?? trigger;
  }

  showSpeechBubble(text: string): void {
    // Remove existing speech bubble
    this.hideSpeechBubble();

    const { width } = this.scene.cameras.main;

    this.speechBubble = this.scene.add.container(width / 2, 90);
    this.speechBubble.setDepth(210);

    // Measure text to size the bubble
    const bubbleText = this.scene.add.text(0, 0, text, {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '13px',
      color: '#ffffff',
      align: 'center',
      wordWrap: { width: 200 },
    }).setOrigin(0.5);

    const bounds = bubbleText.getBounds();
    const padX = 16;
    const padY = 10;
    const bubbleW = bounds.width + padX * 2;
    const bubbleH = bounds.height + padY * 2;
    const tailSize = 8;

    // Bubble background with tail
    const bg = this.scene.add.graphics();
    bg.fillStyle(SPEECH_BUBBLE_BG, 0.95);
    bg.fillRoundedRect(-bubbleW / 2, -bubbleH / 2, bubbleW, bubbleH, SPEECH_BUBBLE_RADIUS);
    bg.lineStyle(2, SPEECH_BUBBLE_BORDER, 0.8);
    bg.strokeRoundedRect(-bubbleW / 2, -bubbleH / 2, bubbleW, bubbleH, SPEECH_BUBBLE_RADIUS);

    // Tail triangle pointing downward
    bg.fillStyle(SPEECH_BUBBLE_BG, 0.95);
    bg.fillTriangle(
      -tailSize, bubbleH / 2,
      tailSize, bubbleH / 2,
      0, bubbleH / 2 + tailSize,
    );

    this.speechBubble.add(bg);
    this.speechBubble.add(bubbleText);

    // Pop-in animation
    this.speechBubble.setScale(0);
    this.scene.tweens.add({
      targets: this.speechBubble,
      scaleX: 1,
      scaleY: 1,
      duration: 250,
      ease: 'Back.easeOut',
    });

    // Auto-hide after 3 seconds
    this.speechBubbleTimeout = this.scene.time.delayedCall(3000, () => {
      this.hideSpeechBubble();
    });
  }

  private hideSpeechBubble(): void {
    if (this.speechBubbleTimeout) {
      this.speechBubbleTimeout.destroy();
      this.speechBubbleTimeout = null;
    }
    if (this.speechBubble) {
      this.speechBubble.destroy();
      this.speechBubble = null;
    }
  }

  // ------------------------------------------------------------------
  // Tutorial completion
  // ------------------------------------------------------------------

  private completeTutorial(): void {
    this.active = false;
    this.enemiesFrozen = false;
    this.overlayContainer.setVisible(false);

    // Kill any lingering tweens
    this.scene.tweens.killTweensOf(this.spotlightBorder);

    eventBus.emit('tutorial_step_complete', { completed: true, level: this.levelConfig.level });
  }

  // ------------------------------------------------------------------
  // Action handler cleanup
  // ------------------------------------------------------------------

  private clearActionHandler(): void {
    if (this.boundActionHandler && this.requiredAction) {
      eventBus.off(this.requiredAction as any, this.boundActionHandler);
      this.boundActionHandler = null;
      this.requiredAction = null;
    }
  }

  // ------------------------------------------------------------------
  // Destroy
  // ------------------------------------------------------------------

  destroy(): void {
    this.clearActionHandler();
    this.hideSpeechBubble();
    if (this.cooldownPauseTimer) {
      this.cooldownPauseTimer.destroy();
      this.cooldownPauseTimer = null;
    }
    this._cooldownsPaused = false;

    // Unregister dialogue triggers
    for (const [trigger, handler] of this.boundDialogueHandlers) {
      const eventName = this.mapTriggerToEvent(trigger);
      if (eventName) {
        eventBus.off(eventName as any, handler);
      }
    }
    this.boundDialogueHandlers.clear();

    // Kill tweens
    if (this.spotlightBorder) {
      this.scene.tweens.killTweensOf(this.spotlightBorder);
    }
    if (this.overlayContainer) {
      this.overlayContainer.destroy();
    }

    this.active = false;
  }
}
